export type GitHubLoadState = 'idle' | 'loading' | 'ready' | 'error';
export type RunPageScreen = 'new-workspace' | 'findings' | 'scope' | 'tools' | 'pricing' | 'settings';

export type RunToolCardKind = 'model' | 'skill';
export type RunToolCoverage = 'free' | 'deep';

export interface RunStatusSnapshotForSettings {
  runId: string;
  target: string;
  scopePath?: string;
  scopePaths?: string[];
  status: string;
}

export interface GitHubAppConfigResponse {
  configured: boolean;
  appSlug?: string;
  installUrl?: string;
  connectUrl?: string;
  missing?: string[];
}

export interface GitHubInstallationSummary {
  id: number;
  account: {
    login: string;
    type?: string;
    avatarUrl?: string;
  };
  targetType?: string;
  repositorySelection?: string;
  htmlUrl?: string;
  appSlug?: string;
}

export interface GitHubRepositorySummary {
  id: number;
  owner: string;
  repo: string;
  fullName: string;
  private: boolean;
  htmlUrl?: string;
  defaultBranch?: string;
  permissions?: Record<string, boolean>;
}

export interface GitHubRepositoryChoice {
  installation: GitHubInstallationSummary;
  repository: GitHubRepositorySummary;
}

export interface GitHubRepositoryBranchSummary {
  name: string;
  default: boolean;
  protected?: boolean;
  commitSha?: string;
}

export interface GitHubRepositoryDirectorySummary {
  path: string;
  name: string;
  depth: number;
}

export interface RunNavItem {
  id: RunPageScreen;
  label: string;
  icon: 'new-workspace' | 'findings' | 'scope' | 'tools' | 'settings';
}

export interface AccountUser {
  id: string;
  githubLogin: string;
  githubAvatarUrl?: string;
  email?: string;
}

export interface Workspace {
  id: string;
  ownerUserId: string;
  name: string;
  slug: string;
  scope?: WorkspaceScope;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceScope {
  installationId: number;
  repositoryId: number;
  branch: string;
  scopePaths: string[];
  assumptionsFilePath?: string;
}

export interface WorkspaceScanSummary {
  id: string;
  workspaceId: string;
  createdByUserId: string;
  scanType: string;
  scanTier?: string;
  target: string;
  status: string;
  sourceType: string;
  branch?: string;
  resultEmail?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface RunToolCard {
  id: string;
  kind: RunToolCardKind;
  coverage: RunToolCoverage;
  title: string;
  description: string;
  logo?: string;
  tag?: string;
}

export const runFormatInstallationName = (installation: GitHubInstallationSummary): string => {
  return installation.account.login || `Installation ${String(installation.id)}`;
};

export const runFormatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const runGetScopeParentPath = (scopePath: string): string | null => {
  const parentSeparatorIndex = scopePath.lastIndexOf('/');

  return parentSeparatorIndex === -1 ? null : scopePath.slice(0, parentSeparatorIndex);
};

export const runGetScopeAncestorPaths = (scopePath: string): string[] => {
  const ancestors: string[] = [];
  let parentPath = runGetScopeParentPath(scopePath);

  while (parentPath) {
    ancestors.unshift(parentPath);
    parentPath = runGetScopeParentPath(parentPath);
  }

  return ancestors;
};
